import React, { useState, useEffect } from 'react';
import {
    Table,
    Tag,
    Card,
    Row,
    Col,
    Select,
    Button,
    Space,
    Modal,
    Form,
    message,
    Typography,
    Flex,
    Input,
    DatePicker,
    Collapse,
    Popconfirm
} from 'antd';
import { PlusOutlined, TeamOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useStudents } from '../../hooks/useStudents';
import { useGroups } from '../../hooks/useGroups';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import dayjs from 'dayjs';
import { assignBulkAchievement, addAchievement, getAchievements, deleteAchievement } from '../../store/slices/achievementsSlice';

const { Text } = Typography;
const { Option } = Select;

// Achievement Rank Enum
const AchievementRank = {
    Championship: 1,    // Şampiyonluk
    SecondPlace: 2,     // 2.lik
    ThirdPlace: 3,      // 3.lük
    FourthPlace: 4,     // 4.lük
    FifthPlace: 5,      // 5.lik
    Participation: 6    // Katılım
};

// Static seasons

const Achievements = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { students, loading: studentsLoading } = useStudents(selectedBranch?.id, selectedPlace?.id);
    const { groups } = useGroups(selectedBranch?.id, selectedPlace?.id);
    const { ageGroups } = useAgeGroups(selectedBranch?.id, selectedPlace?.id);

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [assignAchievementModal, setAssignAchievementModal] = useState(false);
    const [assignForm] = Form.useForm();
    const [bulkAchievementForm] = Form.useForm();

   
    const { achievements, loading: achievementsLoading } = useSelector((state) => state.achievements);
    // Get achievement rank text
    const getAchievementRankText = (rank) => {
        switch (rank) {
            case AchievementRank.Championship:
                return 'Şampiyonluk';
            case AchievementRank.SecondPlace:
                return '2.lik';
            case AchievementRank.ThirdPlace:
                return '3.lük';
            case AchievementRank.FourthPlace:
                return '4.lük';
            case AchievementRank.FifthPlace:
                return '5.lik';
            case AchievementRank.Participation:
                return 'Katılım';
            default:
                return 'Bilinmiyor';
        }
    };

    // Get achievement rank color
    const getAchievementRankColor = (rank) => {
        switch (rank) {
            case AchievementRank.Championship:
                return 'gold';
            case AchievementRank.SecondPlace:
                return 'gold';
            case AchievementRank.ThirdPlace:
                return 'gold';
            case AchievementRank.FourthPlace:
                return 'gold';
            case AchievementRank.FifthPlace:
                return 'gold';
            case AchievementRank.Participation:
                return 'gold';
            default:
                return 'gold';
        }
    };

    const handleAssignAchievement = async (values) => {
        try {
            const achievementData = {
                studentId: selectedStudent,
                season: values.season,
                competitionName: values.competitionName,
                rank: values.achievementRank,
                achievementDate: values.date?.toDate() || new Date()
            };

            // Mock API call - in real app this would be an actual API call
            await dispatch(addAchievement(achievementData)).unwrap();

            message.success('Başarı başarıyla atandı!');
            setAssignAchievementModal(false);
            assignForm.resetFields();
            
            // Başarı eklendikten sonra listeyi yenile
            if (selectedStudent) {
                dispatch(getAchievements({ studentId: selectedStudent }));
            }
        } catch (error) {
            console.error('Assign achievement error:', error);
            message.error(`Başarı atanırken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleBulkAssignAchievement = async () => {
        try {
            const formValues = bulkAchievementForm.getFieldsValue();

            if (!formValues.season) {
                message.warning('Lütfen sezon seçin!');
                return;
            }

            if (!formValues.competitionName) {
                message.warning('Lütfen yarışma/lig adı girin!');
                return;
            }

            if (!formValues.achievementRank) {
                message.warning('Lütfen başarı sıralaması seçin!');
                return;
            }

            if (!formValues.date) {
                message.warning('Lütfen tarih seçin!');
                return;
            }

            const bulkData = {
                branchId: selectedBranch.id,
                placeId: selectedPlace?.id,
                season: formValues.season,
                competitionName: formValues.competitionName,
                rank: formValues.achievementRank,
                achievementDate: formValues.date.toDate()
            };

            // Hangi tür atama yapılacağını belirle
            if (formValues.assignType === 'all') {
                bulkData.assignToAllStudents = true;
            } else if (formValues.assignType === 'group' && formValues.groupId) {
                bulkData.groupId = formValues.groupId;
            } else if (formValues.assignType === 'ageGroup' && formValues.ageGroupId) {
                bulkData.ageGroupId = formValues.ageGroupId;
            }

            await dispatch(assignBulkAchievement(bulkData)).unwrap();
            message.success('Toplu başarı atama başarıyla tamamlandı!');
            bulkAchievementForm.resetFields();
            
            // Toplu başarı atama işleminden sonra listeyi yenile
            if (selectedStudent) {
                dispatch(getAchievements({ studentId: selectedStudent }));
            }
        } catch (error) {
            console.error('Bulk assign achievement error:', error);
            message.error(`Toplu başarı atama sırasında bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleDeleteAchievement = async (record) => {
        try {
            await dispatch(deleteAchievement(record.id)).unwrap();
            message.success('Başarı başarıyla silindi!');
            
            // Başarı silindikten sonra listeyi yenile
            if (selectedStudent) {
                dispatch(getAchievements({ studentId: selectedStudent }));
            }
        } catch (error) {
            console.error('Delete achievement error:', error);
            message.error(`Başarı silinirken bir hata oluştu: ${error.message || error}`);
        }
    };

    useEffect(() => {
        if (selectedStudent) {
            dispatch(getAchievements({ studentId: selectedStudent }));
        }
    }, [selectedStudent]);

    const achievementColumns = [
        {
            title: 'Sezon',
            dataIndex: 'season',
            key: 'season',
        },
        {
            title: 'Yarışma/Lig Adı',
            dataIndex: 'competitionName',
            key: 'competitionName',
        },
        {
            title: 'Başarı Sıralaması',
            dataIndex: 'rank',
            key: 'rank',
            render: (rank) => (
                <Tag color={getAchievementRankColor(rank)}>
                    {getAchievementRankText(rank)}
                </Tag>
            )
        },
        {
            title: 'Tarih',
            dataIndex: 'achievementDate',
            key: 'date',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
            sorter: (a, b) => {
                const dateA = a.achievementDate ? dayjs(a.achievementDate).valueOf() : 0;
                const dateB = b.achievementDate ? dayjs(b.achievementDate).valueOf() : 0;
                return dateB - dateA;
            },
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: 'descend',
            onFilter: (value, record) => {
                return record.achievementDate === value;
            },
            filterMultiple: false,
            filterSearch: true,
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title="Bu başarıyı silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDeleteAchievement(record)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Seçili öğrencinin başarılarını filtrele ve sırala
    const filteredAchievements = selectedStudent
        ? achievements
            .filter(achievement => achievement.studentId === selectedStudent)
            .sort((a, b) => {
                const dateA = a.achievementDate ? dayjs(a.achievementDate).valueOf() : 0;
                const dateB = b.achievementDate ? dayjs(b.achievementDate).valueOf() : 0;
                return dateB - dateA; // Yeni tarihler önce
            })
        : [];

    // Başarı istatistikleri

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Başarıları yönetmek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <Flex vertical gap="middle" style={{ padding: '16px' }}>

            {/* Toplu Başarı Tanımlama Collapse */}
            <Collapse
                items={[
                    {
                        key: '1',
                        label: 'Toplu Başarı Tanımla',
                        children: (
                            <Form form={bulkAchievementForm} layout="vertical">
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="season" label="Sezon">
                                            <Input placeholder="Sezon girin (örn: 2024-2025)" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="competitionName" label="Yarışma/Lig Adı">
                                            <Input placeholder="Yarışma veya lig adı" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="achievementRank" label="Başarı Sıralaması">
                                            <Select placeholder="Başarı sıralaması seçin" style={{ width: '100%' }}>
                                                <Option value={AchievementRank.Championship}>Şampiyonluk</Option>
                                                <Option value={AchievementRank.SecondPlace}>2.lik</Option>
                                                <Option value={AchievementRank.ThirdPlace}>3.lük</Option>
                                                <Option value={AchievementRank.FourthPlace}>4.lük</Option>
                                                <Option value={AchievementRank.FifthPlace}>5.lik</Option>
                                                <Option value={AchievementRank.Participation}>Katılım</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="date" label="Tarih">
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                format="DD/MM/YYYY"
                                                placeholder="Tarih seçin"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="assignType" label="Atama Türü" initialValue="all">
                                            <Select placeholder="Atama türü seçin">
                                                <Option value="all">Bütün Aktif Öğrencilere</Option>
                                                <Option value="group">Gruba Göre</Option>
                                                <Option value="ageGroup">Yaş Grubuna Göre</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) => prevValues.assignType !== currentValues.assignType}
                                        >
                                            {({ getFieldValue }) => {
                                                const assignType = getFieldValue('assignType');

                                                if (assignType === 'group') {
                                                    return (
                                                        <Form.Item
                                                            name="groupId"
                                                            label="Grup Seçin"
                                                            rules={[{ required: true, message: 'Lütfen grup seçin!' }]}
                                                        >
                                                            <Select placeholder="Grup seçin">
                                                                {groups.map(group => (
                                                                    <Option key={group.id} value={group.id}>
                                                                        {group.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }

                                                if (assignType === 'ageGroup') {
                                                    return (
                                                        <Form.Item
                                                            name="ageGroupId"
                                                            label="Yaş Grubu Seçin"
                                                            rules={[{ required: true, message: 'Lütfen yaş grubu seçin!' }]}
                                                        >
                                                            <Select placeholder="Yaş grubu seçin">
                                                                {ageGroups.map(ageGroup => (
                                                                    <Option key={ageGroup.id} value={ageGroup.id}>
                                                                        {ageGroup.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }

                                                return <Form.Item label="Hedef Kitle">
                                                    <Text type="secondary">Bütün aktif öğrencilere başarı atanacak</Text>
                                                </Form.Item>;
                                            }}
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={24}>
                                        <Popconfirm
                                            title="Toplu Başarı Atama"
                                            description="Bu işlem geri alınamaz. Emin misiniz?"
                                            onConfirm={handleBulkAssignAchievement}
                                            okText="Evet"
                                            cancelText="Hayır"
                                            placement="top"
                                        >
                                            <Button
                                                type="primary"
                                                icon={<TeamOutlined />}
                                                size="large"
                                                style={{ width: '100%' }}
                                            >
                                                Toplu Başarı Ata
                                            </Button>
                                        </Popconfirm>
                                    </Col>
                                </Row>
                            </Form>
                        )
                    }
                ]}
            />

            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Öğrenci Seçiniz"
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={setSelectedStudent}
                            loading={studentsLoading}
                        >
                            {students.map(student => (
                                <Option key={student.id} value={student.id}>
                                    {student.fullName}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setAssignAchievementModal(true)}
                            disabled={!selectedStudent}
                        >
                            Yeni Başarı Ekle
                        </Button>
                    </Col>
                </Row>
            </Card>

            {selectedStudent && (
                <Table
                    columns={achievementColumns}
                    dataSource={achievements}
                    rowKey="id"
                    loading={achievementsLoading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başarı`
                    }}
                    locale={{
                        emptyText: achievementsLoading ? 'Yükleniyor...' : 'Henüz başarı bulunmamaktadır'
                    }}
                />
            )}

            {/* Başarı Ekleme Modalı */}
            <Modal
                title="Yeni Başarı Ekle"
                open={assignAchievementModal}
                onCancel={() => {
                    setAssignAchievementModal(false);
                    assignForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={assignForm}
                    layout="vertical"
                    onFinish={handleAssignAchievement}
                >
                    <Form.Item
                        name="season"
                        label="Sezon"
                        rules={[{ required: true, message: 'Lütfen sezon girin!' }]}
                    >
                        <Input placeholder="Sezon girin (örn: 2024-2025)" />
                    </Form.Item>

                    <Form.Item
                        name="competitionName"
                        label="Yarışma/Lig Adı"
                        rules={[{ required: true, message: 'Lütfen yarışma/lig adı girin!' }]}
                    >
                        <Input placeholder="Yarışma veya lig adı" />
                    </Form.Item>

                    <Form.Item
                        name="achievementRank"
                        label="Başarı Sıralaması"
                        rules={[{ required: true, message: 'Lütfen başarı sıralaması seçin!' }]}
                    >
                        <Select placeholder="Başarı sıralaması seçin" allowClear>
                            <Option value={AchievementRank.Championship}>Şampiyonluk</Option>
                            <Option value={AchievementRank.SecondPlace}>2.lik</Option>
                            <Option value={AchievementRank.ThirdPlace}>3.lük</Option>
                            <Option value={AchievementRank.FourthPlace}>4.lük</Option>
                            <Option value={AchievementRank.FifthPlace}>5.lik</Option>
                            <Option value={AchievementRank.Participation}>Katılım</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="date"
                        label="Tarih"
                        rules={[{ required: true, message: 'Lütfen tarih seçin!' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Tarih seçin"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Başarı Ekle
                            </Button>
                            <Button onClick={() => {
                                setAssignAchievementModal(false);
                                assignForm.resetFields();
                            }}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

        </Flex>
    );
};

export default Achievements;